import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProfileReviews = ({ reviews, averageRating, totalReviews }) => {
  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            strokeWidth={2}
            className={star <= rating ? 'text-accent fill-accent' : 'text-muted-foreground'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-2xl font-semibold text-foreground">
          Reviews & Ratings
        </h2>
        <div className="flex items-center space-x-2">
          <Icon name="Star" size={24} className="text-accent fill-accent" strokeWidth={2} />
          <span className="font-heading text-2xl font-bold text-foreground">
            {averageRating}
          </span>
          <span className="font-body text-sm text-muted-foreground">
            ({totalReviews} reviews)
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {reviews?.map((review) => (
          <div
            key={review?.id}
            className="bg-background rounded-lg p-6 border border-border"
          >
            <div className="flex items-start space-x-4 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                {review?.ownerPhoto ? (
                  <Image
                    src={review?.ownerPhoto}
                    alt={`${review?.ownerName}'s photo`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center">
                    <Icon name="User" size={24} className="text-muted-foreground" />
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-lg font-semibold text-foreground">
                    {review?.ownerName}
                  </h3>
                  <span className="font-caption text-sm text-muted-foreground">
                    {formatDate(review?.createdAt)}
                  </span>
                </div>

                <div className="flex items-center space-x-4 mb-3">
                  {renderStars(review?.overallRating)}
                  <span className="font-body text-sm font-semibold text-foreground">
                    {review?.overallRating}/5
                  </span>
                </div>

                {/* Category Ratings */}
                {review?.categoryRatings && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                    {Object.entries(review?.categoryRatings)?.map(([category, rating]) => (
                      <div key={category} className="text-center">
                        <p className="font-caption text-xs text-muted-foreground capitalize mb-1">
                          {category}
                        </p>
                        <div className="flex items-center justify-center space-x-1">
                          <Icon name="Star" size={12} className="text-accent fill-accent" />
                          <span className="font-body text-sm font-semibold text-foreground">
                            {rating}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Review Comment */}
                {review?.comment && (
                  <p className="font-body text-base text-muted-foreground leading-relaxed">
                    {review?.comment}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProfileReviews;